import { useEffect } from "react"
import { create } from "zustand"

const STORAGE_KEY = "fibonacci_form_session"
const FIRST_LANDING_DELAY_SECONDS = 20 // auto-open once after user lands on the site
const SHOW_INTERVALS = [60, 120, 240] // subsequent show delays after the first
const REPEAT_INTERVAL = 300 // keep repeating every 5 minutes after the above
const INCOMPLETE_RESET_SECONDS = 15 * 60 // 15 minutes of inactivity resets the session

function getNextShowDelay(showCount: number): number {
  if (showCount === 0) return FIRST_LANDING_DELAY_SECONDS
  if (showCount - 1 < SHOW_INTERVALS.length) return SHOW_INTERVALS[showCount - 1]!
  return REPEAT_INTERVAL
}


export const useSessionTracker = () => {
  useEffect(() => {
    const SESSION_KEY = "sessionDate";
    const COMPARE_KEY = "comparedProperties";

    const now = new Date().getTime();
    const storedSession = localStorage.getItem(SESSION_KEY);

    if (!storedSession) {
      localStorage.setItem(SESSION_KEY, now.toString());
    } else {
      const lastSession = parseInt(storedSession, 10);
      const hoursElapsed = (now - lastSession) / (1000 * 60 * 60);

      if (hoursElapsed >= 24) {
        localStorage.setItem(SESSION_KEY, now.toString());
        localStorage.setItem(COMPARE_KEY, JSON.stringify([]));
      }
    }
  }, []);
};




type SessionState = {
  startedAt: string
  status: "pending" | "cancelled" | "completed"
  showCount: number
  lastLeftAt?: string
}

interface FibonacciFormStore {
  shouldShow: boolean
  session: SessionState | null
  refresh: () => void
  markCancelled: () => void
  markCompleted: () => void
  markPendingLeave: () => void
  resetTimer: () => void
}

function getStored(): SessionState | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  try {
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveStored(state: SessionState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

function now(): Date {
  return new Date()
}

function createInitialSession(): SessionState {
  return {
    startedAt: now().toISOString(),
    status: "cancelled",
    showCount: 0,
  }
}

function calcRemainingTime(session: SessionState | null): number | null {
  // If no session or completed, return null (no timer active)
  if (!session || session.status === "completed") return null

  // If pending, return 0 (should show now)
  if (session.status === "pending") return 0

  // Calculate remaining time for cancelled sessions
  const startedAt = new Date(session.startedAt)
  const diffSec = Math.floor((now().getTime() - startedAt.getTime()) / 1000)

  const nextDelay = getNextShowDelay(session.showCount)
  const remaining = nextDelay - diffSec
  return remaining > 0 ? remaining : 0
}

function calcShouldShow(session: SessionState | null): { shouldShow: boolean; newSession?: SessionState } {
  // First visit: start a cancelled session and wait the initial interval before first show
  if (!session) {
    return { shouldShow: false, newSession: createInitialSession() }
  }

  if (session.status === "completed") return { shouldShow: false }
  if (session.status === "pending" && session.lastLeftAt) {
    const lastLeftAt = new Date(session.lastLeftAt)
    const elapsedSinceLeave = Math.floor((now().getTime() - lastLeftAt.getTime()) / 1000)

    if (elapsedSinceLeave >= INCOMPLETE_RESET_SECONDS) {
      return { shouldShow: false, newSession: createInitialSession() }
    }

    const reset = {
      startedAt: now().toISOString(),
      status: "cancelled" as const,
      showCount: 0,
    }
    return { shouldShow: false, newSession: reset }
  }
  if (session.status === "pending") return { shouldShow: true }

  // cancelled → check if interval passed
  const startedAt = new Date(session.startedAt)
  const diffSec = Math.floor((now().getTime() - startedAt.getTime()) / 1000)

  const nextDelay = getNextShowDelay(session.showCount)

  if (diffSec >= nextDelay) {
    const updated = {
      startedAt: now().toISOString(),
      status: "pending" as const,
      showCount: session.showCount + 1,
    }
    return { shouldShow: true, newSession: updated }
  }
  return { shouldShow: false }
}

export const useFibonacciFormStore = create<FibonacciFormStore>((set) => ({
  shouldShow: false,
  session: null,

  refresh: () => {
    const session = getStored()
    const { shouldShow, newSession } = calcShouldShow(session)
    if (newSession) saveStored(newSession)
    set({ shouldShow, session: newSession || session })
  },

  markCancelled: () => {
    const session = getStored()
    if (session) {
      const updated = { ...session, status: "cancelled" as const, startedAt: now().toISOString(), lastLeftAt: undefined }
      saveStored(updated)
      set({ shouldShow: false, session: updated })
    }
  },

  markCompleted: () => {
    const session = getStored()
    if (session) {
      const updated = { ...session, status: "completed" as const, startedAt: now().toISOString(), lastLeftAt: undefined }
      saveStored(updated)
      set({ shouldShow: false, session: updated })
    }
  },
  markPendingLeave: () => {
    const session = getStored()
    if (session && session.status === "pending") {
      const updated = { ...session, lastLeftAt: now().toISOString() }
      saveStored(updated)
      set({ session: updated })
    }
  },
  resetTimer: () => {
    const session = getStored()
    if (session && session.status === "cancelled") {
      // Reset the timer by updating startedAt to now
      const updated = { ...session, startedAt: now().toISOString() }
      saveStored(updated)
      set({ session: updated })
    }
  },
}))


export function useShowFibonacciForm() {
  const shouldShow = useFibonacciFormStore((s) => s.shouldShow)
  const refresh = useFibonacciFormStore((s) => s.refresh)
  const markPendingLeave = useFibonacciFormStore((s) => s.markPendingLeave)
  const resetTimer = useFibonacciFormStore((s) => s.resetTimer)

  useEffect(() => {
    refresh() // run on mount


    // re-check every 1s so the 15s trigger is accurate
    const interval = setInterval(refresh, 1000)

    // cross-tab sync
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        refresh()
      }
    }
    window.addEventListener("storage", onStorage)

    return () => {
      clearInterval(interval)
      window.removeEventListener("storage", onStorage)
    }
  }, [refresh])

  useEffect(() => {
    const handleUnload = () => markPendingLeave()
    window.addEventListener("beforeunload", handleUnload)
    window.addEventListener("pagehide", handleUnload)

    return () => {
      window.removeEventListener("beforeunload", handleUnload)
      window.removeEventListener("pagehide", handleUnload)
    }
  }, [markPendingLeave])

  // Keyboard activity detection - reset timer if less than 10s remaining
  useEffect(() => {
    const handleKeyboardActivity = () => {
      const session = getStored()
      if (!session) return

      // Only check and reset for cancelled sessions (not pending or completed)
      if (session.status === "cancelled") {
        const remainingTime = calcRemainingTime(session)

        // If remaining time is less than 10 seconds, reset the timer
        if (remainingTime !== null && remainingTime > 0 && remainingTime < 10) {
          resetTimer()
        }
      }
    }

    // Listen for keyboard events
    window.addEventListener("keydown", handleKeyboardActivity)
    window.addEventListener("keyup", handleKeyboardActivity)

    return () => {
      window.removeEventListener("keydown", handleKeyboardActivity)
      window.removeEventListener("keyup", handleKeyboardActivity)
    }
  }, [resetTimer])

  return shouldShow
}